import { useEffect, useState } from "react";
import { API, BACKEND_URL } from "../lib/constants";
import { Input } from "../components/ui/input";
import Logo from "../components/Logo";
import {
  Lock,
  Mail,
  Phone,
  MapPin,
  User,
  Ruler,
  Image as ImageIcon,
  LogOut,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "new", label: "Nieuw", color: "#00E5FF" },
  { value: "contacted", label: "Gecontacteerd", color: "#C5A059" },
  { value: "quoted", label: "Offerte verstuurd", color: "#A78BFA" },
  { value: "won", label: "Gewonnen", color: "#34D399" },
  { value: "lost", label: "Verloren", color: "#F87171" },
];

const statusMeta = (s) =>
  STATUSES.find((x) => x.value === s) || STATUSES[0];

export default function Admin() {
  const [token, setToken] = useState(
    () => localStorage.getItem("jn_admin_token") || ""
  );
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("all");

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) throw new Error("Ongeldig wachtwoord");
      const data = await res.json();
      setToken(data.token);
      localStorage.setItem("jn_admin_token", data.token);
      toast.success("Ingelogd");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        setToken("");
        localStorage.removeItem("jn_admin_token");
        return;
      }
      const data = await res.json();
      setLeads(data);
    } catch {
      toast.error("Kon aanvragen niet laden");
    }
  };

  useEffect(() => {
    if (token) fetchLeads();
    // eslint-disable-next-line
  }, [token]);

  const logout = () => {
    setToken("");
    localStorage.removeItem("jn_admin_token");
  };

  const updateStatus = async (leadId, status) => {
    try {
      const res = await fetch(`${API}/admin/leads/${leadId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Update mislukt");
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status } : l))
      );
      toast.success("Status bijgewerkt");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const exportCsv = () => {
    if (!token) return;
    const url = `${API}/admin/leads.csv?token=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6">
        <form
          onSubmit={login}
          className="jn-glass rounded-3xl p-8 md:p-10 w-full max-w-md"
          data-testid="admin-login-form"
        >
          <Logo size="text-2xl" />
          <div className="mt-6 mb-2 text-xs font-bold uppercase tracking-[0.25em] text-cyan-400">
            Admin
          </div>
          <h1 className="font-heading text-2xl md:text-3xl text-white tracking-tight">
            Inloggen
          </h1>
          <div className="mt-6 flex items-center gap-3 border border-white/10 rounded-xl px-4 py-1 bg-white/[0.03]">
            <Lock size={16} className="text-zinc-500" />
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Admin wachtwoord"
              className="border-0 bg-transparent text-white placeholder:text-zinc-600 focus-visible:ring-0"
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-6 jn-btn-primary w-full px-6 py-3 rounded-full text-sm disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Bezig..." : "Inloggen"}
          </button>
        </form>
      </div>
    );
  }

  const filteredLeads =
    filter === "all" ? leads : leads.filter((l) => (l.status || "new") === filter);
  const selected = leads.find((l) => l.id === selectedId) || null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-40 jn-glass-strong">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo size="text-xl" />
            <span className="text-xs uppercase tracking-[0.25em] text-zinc-500 hidden sm:inline">
              Lead dashboard
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              className="jn-btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-2"
              data-testid="admin-export-csv"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={logout}
              className="jn-btn-ghost px-4 py-2 rounded-full text-xs inline-flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl text-white tracking-tight">
              Aanvragen
            </h1>
            <p className="mt-2 text-sm text-zinc-400 font-body">
              {leads.length} {leads.length === 1 ? "aanvraag" : "aanvragen"}{" "}
              totaal
            </p>
          </div>
          <button
            onClick={fetchLeads}
            className="jn-btn-ghost px-4 py-2 rounded-full text-xs"
            data-testid="admin-refresh"
          >
            Vernieuwen
          </button>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <FilterPill
            active={filter === "all"}
            onClick={() => setFilter("all")}
            count={leads.length}
            label="Alle"
            color="#ffffff"
            testid="filter-all"
          />
          {STATUSES.map((s) => (
            <FilterPill
              key={s.value}
              active={filter === s.value}
              onClick={() => setFilter(s.value)}
              count={
                leads.filter((l) => (l.status || "new") === s.value).length
              }
              label={s.label}
              color={s.color}
              testid={`filter-${s.value}`}
            />
          ))}
        </div>

        {filteredLeads.length === 0 ? (
          <div
            className="jn-glass rounded-3xl p-12 text-center text-zinc-400 font-body"
            data-testid="admin-empty-state"
          >
            Geen aanvragen{filter !== "all" ? " in deze status" : ""}.
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-3 max-h-[70vh] overflow-auto pr-1">
              {filteredLeads.map((l) => {
                const meta = statusMeta(l.status || "new");
                return (
                  <button
                    key={l.id}
                    onClick={() => setSelectedId(l.id)}
                    className={`w-full text-left jn-glass jn-card-hover rounded-2xl p-4 border ${
                      selectedId === l.id
                        ? "border-cyan-400/50"
                        : "border-transparent"
                    }`}
                    data-testid={`lead-item-${l.id}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-heading text-white font-medium truncate">
                        {l.naam}
                      </div>
                      <span
                        className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 rounded-full border"
                        style={{
                          color: meta.color,
                          borderColor: `${meta.color}55`,
                          background: `${meta.color}10`,
                        }}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-400 font-body truncate">
                      {l.email} · {l.gemeente}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      {new Date(l.created_at).toLocaleString("nl-BE")}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="lg:col-span-2">
              {selected ? (
                <LeadDetail
                  lead={selected}
                  onStatusChange={(s) => updateStatus(selected.id, s)}
                />
              ) : (
                <div className="jn-glass rounded-3xl p-12 text-center text-zinc-400 font-body h-full flex items-center justify-center">
                  Selecteer een aanvraag om details te zien.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function FilterPill({ active, onClick, count, label, color, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-3.5 py-1.5 rounded-full text-xs font-body transition border ${
        active
          ? "bg-white/10 text-white border-white/20"
          : "text-zinc-400 border-white/8 hover:text-white hover:border-white/20"
      }`}
      data-testid={testid}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle"
        style={{ background: color }}
      />
      {label}
      <span className="ml-1.5 text-zinc-500">({count})</span>
    </button>
  );
}

function LeadDetail({ lead, onStatusChange }) {
  const meta = statusMeta(lead.status || "new");
  return (
    <div
      className="jn-glass rounded-3xl p-8"
      data-testid={`lead-detail-${lead.id}`}
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-400 mb-2">
            {lead.klant_type}
          </div>
          <h2 className="font-heading text-3xl text-white tracking-tight">
            {lead.naam}
          </h2>
          <div className="mt-2 text-xs text-zinc-500">
            {new Date(lead.created_at).toLocaleString("nl-BE")}
          </div>
        </div>
        <div
          className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.22em] border"
          style={{
            color: meta.color,
            borderColor: `${meta.color}55`,
            background: `${meta.color}10`,
          }}
        >
          {meta.label}
        </div>
      </div>

      {/* Status changer */}
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
          Status wijzigen
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => {
            const active = (lead.status || "new") === s.value;
            return (
              <button
                key={s.value}
                onClick={() => !active && onStatusChange(s.value)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-body border transition ${
                  active
                    ? "text-black"
                    : "text-zinc-300 border-white/10 hover:border-white/30"
                }`}
                style={
                  active
                    ? {
                        background: s.color,
                        borderColor: s.color,
                      }
                    : {}
                }
                data-testid={`status-btn-${s.value}`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-7 grid sm:grid-cols-2 gap-4 text-sm">
        <Row icon={Mail} label="E-mail" value={
          <a href={`mailto:${lead.email}`} className="text-cyan-400 hover:underline">
            {lead.email}
          </a>
        } />
        <Row icon={Phone} label="Telefoon" value={
          <a href={`tel:${lead.telefoon}`} className="text-cyan-400 hover:underline">
            {lead.telefoon}
          </a>
        } />
        <Row icon={MapPin} label="Gemeente" value={lead.gemeente} />
        <Row icon={User} label="Type" value={lead.klant_type} />
        <Row icon={Ruler} label="Oplossing" value={(lead.oplossing || "").replace(/_/g, " ")} />
        <Row icon={Ruler} label="Afmetingen" value={lead.afmetingen || "—"} />
      </div>

      {lead.bericht && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Bericht
          </div>
          <div className="jn-glass rounded-2xl p-4 text-zinc-300 text-sm whitespace-pre-wrap font-body">
            {lead.bericht}
          </div>
        </div>
      )}

      {lead.photo_url && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center gap-2">
            <ImageIcon size={12} /> Bijlage
          </div>
          <a
            href={`${BACKEND_URL}${lead.photo_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-2xl overflow-hidden border border-white/10 hover:border-cyan-400/50 transition"
            data-testid="lead-photo-link"
          >
            <img
              src={`${BACKEND_URL}${lead.photo_url}`}
              alt="attachment"
              className="w-full max-h-96 object-cover"
            />
          </a>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 flex items-center gap-2">
        <Icon size={12} /> {label}
      </div>
      <div className="text-white font-body">{value}</div>
    </div>
  );
}
